import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  LinearProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

const FileUpload = ({ projectId, onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileMetadata, setFileMetadata] = useState(null);
  const fileInputRef = useRef(null);

  // File validation constants (matching backend)
  const MAX_FILE_SIZE = 600 * 1024 * 1024; // 600MB
  const ALLOWED_FORMATS = ['.wav', '.mp3'];

  const validateFile = (file) => {
    // Check file extension
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_FORMATS.includes(fileExt)) {
      return `Invalid file format. Allowed formats: ${ALLOWED_FORMATS.join(', ')}`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size (${MAX_FILE_SIZE / (1024 * 1024)}MB)`;
    }

    // Check for path traversal in filename
    if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
      return 'Invalid filename: path traversal detected';
    }

    return null;
  };

  const handleFileSelect = (file) => {
    // Validate file
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
    setUploadSuccess(false);
    setFileMetadata(null);
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !projectId) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      // Upload with progress tracking
      const result = await apiService.uploadFile(
        projectId,
        selectedFile,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      );

      setFileMetadata(result);
      setUploadSuccess(true);
      setUploadProgress(100);

      // Notify parent component
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      // Reset after a delay
      setTimeout(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadSuccess(false);
        setFileMetadata(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 3000);
    } catch (error) {
      if (error.response?.data?.detail) {
        setUploadError(error.response.data.detail);
      } else {
        setUploadError(error.message || 'Upload failed');
      }
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    // Note: axios doesn't provide built-in cancellation in this setup
    // For full cancellation support, we'd need to use axios CancelToken
    setUploading(false);
    setUploadProgress(0);
    setUploadError('Upload cancelled by user');
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadError(null);
    setUploadSuccess(false);
    setFileMetadata(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".wav,.mp3"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Drop Zone */}
      <Paper
        elevation={dragOver ? 8 : 2}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: 'center',
          border: dragOver ? '2px dashed primary.main' : '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'grey.400',
          backgroundColor: dragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.3s',
        }}
        onClick={!uploading && !selectedFile ? handleBrowseClick : undefined}
      >
        {!selectedFile && (
          <>
            <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drag and drop an audio file here
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              or
            </Typography>
            <Button variant="contained" sx={{ mt: 2 }}>
              Browse Files
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
              Supported formats: WAV, MP3 | Max size: 600MB
            </Typography>
          </>
        )}

        {selectedFile && !uploading && !uploadSuccess && (
          <Box>
            <List>
              <ListItem>
                <ListItemText
                  primary={selectedFile.name}
                  secondary={formatFileSize(selectedFile.size)}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={handleRemoveFile}>
                    <CloseIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              startIcon={<UploadIcon />}
              sx={{ mt: 2 }}
            >
              Upload File
            </Button>
          </Box>
        )}

        {uploading && (
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" gutterBottom>
              Uploading {selectedFile.name}...
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {uploadProgress}%
            </Typography>
            <Button onClick={handleCancel} variant="outlined" sx={{ mt: 2 }}>
              Cancel Upload
            </Button>
          </Box>
        )}

        {uploadSuccess && fileMetadata && (
          <Box>
            <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              Upload Successful!
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="File"
                  secondary={fileMetadata.original_filename}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Size"
                  secondary={formatFileSize(fileMetadata.file_size)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Duration"
                  secondary={formatDuration(fileMetadata.duration)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Audio"
                  secondary={
                    <Box>
                      <Chip
                        label={`${fileMetadata.sample_rate} Hz`}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`${fileMetadata.channels} channel${fileMetadata.channels > 1 ? 's' : ''}`}
                        size="small"
                      />
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {uploadError && (
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          onClose={() => setUploadError(null)}
          sx={{ mt: 2 }}
        >
          {uploadError}
        </Alert>
      )}
    </Box>
  );
};

export default FileUpload;
